import { useState, useEffect } from "react";
import { api, handleAuthFailure } from "../data/api";
import { generateDeviceId } from "../data/ids";
import { isMultiChannelDevice, maxChannelsForDevice } from "../data/deviceModels";
import Badge from "../components/Badge";
import Pill from "../components/Pill";
import Btn from "../components/Btn";
import DeviceCombobox from "../components/DeviceCombobox";
import UndoConfirm from "../components/UndoConfirm";
import Input from "../components/Input";
import { TableWrap, TH, TD } from "../components/TableWrap";
import { useTheme } from "../context/ThemeContext";

const validMAC = (mac) =>
  /^([0-9A-Fa-f]{2}:){5}[0-9A-Fa-f]{2}$/.test(mac.trim());

export default function EditAffiliate({ affiliateId, onBack, onSaved }) {
  const { mode } = useTheme();
  const [data, setData]                     = useState(null);
  const [allChannels, setAllChannels]       = useState([]);
  const [allModels, setAllModels]           = useState([]);
  const [takenChannels, setTakenChannels]   = useState([]);
  const [existingDevices, setExistingDevices] = useState([]);
  const [editingChannelFor, setEditingChannelFor] = useState(null);
  const [pendingChannelRemoval, setPendingChannelRemoval] = useState(null); // { deviceId, channelId, channelUrl }
  const [optimisticallyRemoved, setOptimisticallyRemoved] = useState([]); // [{ deviceId, channelId }]
  const [channelDraft, setChannelDraft]           = useState('');
  const [changingChannel, setChangingChannel]     = useState(false);
  const [channelChangeError, setChannelChangeError] = useState(null);
  const [loading, setLoading]               = useState(true);
  const [saving, setSaving]                 = useState(false);
  const [error, setError]                   = useState(null);
  const [success, setSuccess]               = useState(null);
  const [form, setForm]                     = useState(null);
  const [newDevices, setNewDevices]         = useState([]);
  const [removedChannels, setRemovedChannels] = useState([]);
  const [removedDevices, setRemovedDevices] = useState([]);
  const [addedChannels, setAddedChannels]   = useState([]);
  const [allAffiliates, setAllAffiliates] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [detail, chs, taken, models, devs, affs] = await Promise.all([
        api.affiliates.detail(affiliateId),
        api.channels.list().catch(() => []),
        api.devices.takenChannels().catch(() => []),
        api.devices.models().catch(() => []),
        api.devices.list({}).catch(() => []),
        api.affiliates.list(),
      ]);
      setData(detail);
      setAllChannels(chs);
      setTakenChannels(taken);
      setAllModels(models);
      setAllAffiliates(affs);
      setExistingDevices(devs);
      setForm({
        org_name:      detail.affiliate.org_name      || "",
        contact_name:  detail.affiliate.contact_name  || "",
        contact_email: detail.affiliate.contact_email || "",
        region:        detail.affiliate.region        || "",
        timezone:      detail.affiliate.timezone      || "",
        parent_id:     detail.affiliate.parent_id      || "",
      });
    } catch (e) {
      if (handleAuthFailure(e)) return;
      setError("Failed to load affiliate. Is the server running?");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [affiliateId]);

  const f = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  // ── Change channel on an already-saved device ───────────────────────────
  const handleChangeChannel = async (deviceId) => {
    if (!channelDraft) { setEditingChannelFor(null); return; }
    setChangingChannel(true);
    setChannelChangeError(null);
    try {
      const ch = allChannels.find((c) => c.id === channelDraft);
      await api.devices.assignChannel(deviceId, {
        channel_id:   channelDraft,
        channel_url:  ch?.master_srt_url || "",
        display_port: 1,
        audio_device: "default",
      });
      await load(); // refresh data so the table reflects the new channel
      setEditingChannelFor(null);
    } catch (e) {
      setChannelChangeError(e.message || "Failed to change channel");
    } finally {
      setChangingChannel(false);
    }
  };

  // ── Select existing device into a new row ──────────────────────────────
  const selectExistingDevice = (index, deviceId) => {
    if (!deviceId) {
      // Reset back to new device entry
      setNewDevices((p) => p.map((d, j) => j === index
        ? { deviceType:"box", mac:"", model: allModels[0] || "", channelId:"", ip_address:"", isNew:true, existingId:null }
        : d
      ));
      return;
    }
    const dev = existingDevices.find((d) => d.id === deviceId);
    if (!dev) return;
    setNewDevices((p) => p.map((d, j) => j === index
      ? {
          ...d,
          deviceType: dev.device_type || "box",
          mac:        dev.mac_address,
          model:      dev.device_model || allModels[0] || "",
          ip_address: dev.ip_address   || "",
          isNew:      false,
          existingId: dev.id,
        }
      : d
    ));
  };

  // ── Derived state ──────────────────────────────────────────────────────
  const assignedIds = data?.channels?.map((c) => c.id) || [];
  const currentChannels = [
    ...(data?.channels || []).filter((c) => !removedChannels.includes(c.id)),
    ...allChannels.filter((c) => addedChannels.includes(c.id)),
  ];
  const currentDevices = (data?.devices || []).filter(
    (d) => !removedDevices.includes(d.id)
  );
  // Channels are shared infrastructure — multiple affiliates can use the same
  // channel. Only exclude channels already assigned to THIS affiliate.
  const availableToAdd = allChannels.filter(
    (c) => !assignedIds.includes(c.id) && !addedChannels.includes(c.id)
  );
  // Every channel id bound to a device row, across single + multi-channel and
  // both the existing-device shape (channel_id/channel_ids) and the new-row
  // shape (channelId/channelIds).
  const deviceChannelIds = (d) => {
    const multi = Array.isArray(d.channel_ids) ? d.channel_ids
                : Array.isArray(d.channelIds)  ? d.channelIds : [];
    if (multi.length) return multi.filter(Boolean);
    const single = d.channel_id || d.channelId;
    return single ? [single] : [];
  };
  const deviceTypeOf = (d) => d.device_type || d.deviceType || "box";

  // Channels blocked for a new-device row: those already used by same-type
  // devices — existing devices plus other new rows. A channel may appear once
  // per device type (box / self-srt) per affiliate; different types never
  // block each other.
  const channelsBlockedForRow = (index) => {
    const type = deviceTypeOf(newDevices[index]);
    const fromExisting = currentDevices
      .filter((d) => deviceTypeOf(d) === type)
      .flatMap(deviceChannelIds);
    const fromOtherNew = newDevices
      .filter((_, j) => j !== index)
      .filter((d) => deviceTypeOf(d) === type)
      .flatMap(deviceChannelIds);
    return [...new Set([...fromExisting, ...fromOtherNew])];
  };

  // ── Save handler ───────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!form.org_name?.trim())      { setError("Organization name is required"); return; }
    // if (!form.contact_email?.trim()) { setError("Contact email is required");     return; }

    for (let i = 0; i < newDevices.length; i++) {
      const dev = newDevices[i];
      if (dev.isNew === false) continue; // existing device, no new-row validation needed
      if (dev.deviceType === "self-srt-connect") {
        if (dev.channelId && !dev.ip_address?.trim()) {
          setError(`Device ${i + 1}: IP address is required for Self-SRT-Connect`);
          return;
        }
        if (dev.ip_address?.trim() && !/^(\d{1,3}\.){3}\d{1,3}$/.test(dev.ip_address.trim())) {
          setError(`Device ${i + 1}: Enter a valid IPv4 address`);
          return;
        }
      } else if (dev.mac && !validMAC(dev.mac)) {
        setError(`Device ${i + 1}: Invalid MAC address format`);
        return;
      }
    }

    setSaving(true);
    setError(null);
    try {
      // 1. Update basic details
      await api.affiliates.update(affiliateId, form);

      // 2. Remove channels
      for (const chId of removedChannels) {
        await api.affiliates.removeChannel(affiliateId, chId);
      }

      // 3. Add new channels
      if (addedChannels.length > 0) {
        await api.affiliates.assignChannels(affiliateId, addedChannels);
      }

      // 4. Unlink removed devices
      for (const devId of removedDevices) {
        await api.devices.unlinkAffiliate(devId);
      }

      // 5. Process new device rows
      for (let i = 0; i < newDevices.length; i++) {
        const dev = newDevices[i];
        const chIds = isMultiChannelDevice(dev)
          ? (Array.isArray(dev.channelIds) ? dev.channelIds : (dev.channelId ? [dev.channelId] : []))
          : (dev.channelId ? [dev.channelId] : []);
        if (chIds.length === 0) continue;

        let deviceId;

        if (!dev.isNew && dev.existingId) {
          deviceId = dev.existingId;
          await api.devices.patch(deviceId, { affiliate_id: affiliateId });
        } else {
          const isSelfSrt = dev.deviceType === "self-srt-connect";
          if (isSelfSrt) {
            if (!dev.ip_address?.trim()) continue;
          } else {
            if (!dev.mac?.trim()) continue;
          }
          const nextId = generateDeviceId();

          const newDev = await api.devices.create({
            id:           nextId,
            device_type:  isSelfSrt ? 'self-srt-connect' : 'box',
            mac_address:  isSelfSrt ? null : dev.mac.trim().toUpperCase(),
            device_model: isSelfSrt ? null : dev.model,
            affiliate_id: affiliateId,
            ip_address:   isSelfSrt ? dev.ip_address.trim() : (dev.ip_address?.trim() || null),
            ip_type:      isSelfSrt ? 'static' : (dev.ip_address?.trim() ? "static" : "dhcp"),
          });
          deviceId = newDev.id;
        }

        for (const chId of chIds) {
          const ch = allChannels.find(c => c.id === chId);
          await api.devices.assignChannel(deviceId, {
            channel_id:   chId,
            channel_url:  ch?.master_srt_url || "",
            display_port: 1,
            audio_device: "default",
          });
        }
      }

      setRemovedChannels([]);
      setRemovedDevices([]);
      setAddedChannels([]);
      setNewDevices([]);
      setSuccess("Affiliate updated successfully.");
      setTimeout(() => setSuccess(null), 3000);
      await load();
      onSaved?.();
    } catch (e) {
      setError(e.message || "Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div style={{ fontSize:12, color:"var(--text-dim)", padding:"40px 0", textAlign:"center" }}>
      Loading affiliate…
    </div>
  );

  if (!data) return (
    <div style={{ fontSize:12, color:"var(--red)", padding:"40px 0", textAlign:"center" }}>
      {error || "Affiliate not found."}
    </div>
  );

  return (
    <>
      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:18, paddingBottom:14, borderBottom:`1px solid var(--border)` }}>
        <div>
          <h1 style={{ fontSize:17, fontWeight:700, color:"var(--ink)" }}>Edit Affiliate</h1>
          <div style={{ fontSize:12, color:"var(--text-sub)", marginTop:3, fontFamily:"'IBM Plex Mono', monospace" }}>{affiliateId}</div>
        </div>
        <Btn onClick={() => onBack && onBack()}>← Back to Affiliates</Btn>
      </div>

      {error && (
        <div style={{ background:"var(--red-bg)", border:`1px solid var(--red-border)`, borderRadius:7, padding:"10px 14px", marginBottom:14, fontSize:12, color:"var(--red)" }}>
          {error}
        </div>
      )}
      {success && (
        <div style={{ background:"var(--green-bg)", border:`1px solid var(--green-border)`, borderRadius:7, padding:"10px 14px", marginBottom:14, fontSize:12, color:"var(--green)" }}>
          {success}
        </div>
      )}

      <div style={{ display:"flex", flexDirection:"column", gap:14 }}>

        {/* ── Hierarchy ───────────────────────────────────────────────── */}
        {(() => {
          const thisAff = data?.affiliate;
          if (!thisAff) return null;
          const parent = thisAff.parent_id
            ? allAffiliates.find(a => a.id === thisAff.parent_id)
            : null;
          const children = allAffiliates.filter(a => a.parent_id === affiliateId);
          if (!parent && children.length === 0) return null;

          return (
            <div style={{ background:"var(--surface)", border:`1px solid var(--border)`, borderRadius:9, padding:20 }}>
              <div style={{ fontSize:11, fontWeight:700, color:"var(--text-dim)", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:14 }}>
                Hierarchy
              </div>

              {parent && (
                <div style={{ marginBottom: children.length > 0 ? 14 : 0 }}>
                  <div style={{ fontSize:11, color:"var(--text-sub)", marginBottom:6 }}>This distributer is a affiliate of:</div>
                  <button
                    onClick={() => onBack && onBack(parent.id)}
                    style={{ display:"flex", alignItems:"center", gap:8, background:"var(--surface-alt)", border:`1px solid var(--border)`, borderRadius:7, padding:"8px 12px", cursor:"pointer", fontFamily:"inherit", width:"100%", textAlign:"left" }}
                  >
                    <span style={{ fontSize:13, color:"var(--text-dim)" }}>↑</span>
                    <div>
                      <div style={{ fontSize:12, fontWeight:600, color:"var(--ink)" }}>{parent.org_name}</div>
                      <div style={{ fontSize:10, color:"var(--text-dim)", fontFamily:"'IBM Plex Mono', monospace" }}>{parent.id}</div>
                    </div>
                  </button>
                </div>
              )}

              {children.length > 0 && (
                <div>
                  <div style={{ fontSize:11, color:"var(--text-sub)", marginBottom:6 }}>
                    {children.length} affiliate{children.length !== 1 ? "s" : ""}:
                  </div>
                  <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                    {children.map(child => (
                      <button
                        key={child.id}
                        onClick={() => onBack && onBack(child.id)}
                        style={{ display:"flex", alignItems:"center", gap:8, background:"var(--surface-alt)", border:`1px solid var(--border)`, borderRadius:7, padding:"8px 12px", cursor:"pointer", fontFamily:"inherit", width:"100%", textAlign:"left" }}
                      >
                        <span style={{ fontSize:13, color:"var(--text-dim)" }}>↳</span>
                        <div style={{ flex:1 }}>
                          <div style={{ fontSize:12, fontWeight:600, color:"var(--ink)" }}>{child.org_name}</div>
                          <div style={{ fontSize:10, color:"var(--text-dim)", fontFamily:"'IBM Plex Mono', monospace" }}>{child.id}</div>
                        </div>
                        <Badge status={child.status} />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* ── Basic Details ───────────────────────────────────────────── */}
        <div style={{ background:"var(--surface)", border:`1px solid var(--border)`, borderRadius:9, padding:20 }}>
          <div style={{ fontSize:11, fontWeight:700, color:"var(--text-dim)", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:16 }}>Basic Details</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
            <Input label="Organization Name" value={form.org_name}      onChange={f("org_name")}       />
            <Input label="Contact Name"      value={form.contact_name}  onChange={f("contact_name")}   />
            <Input label="Contact Email"     value={form.contact_email} onChange={f("contact_email")}  />
            <Input label="Region"            value={form.region}        onChange={f("region")}        />
            <Input label="Timezone"          value={form.timezone}      onChange={f("timezone")}       />

            <div>
              <div style={{ fontSize:11, color:"var(--text-dim)", marginBottom:5, fontWeight:600 }}>Parent Affiliate</div>
              <select
                value={form.parent_id || ""}
                onChange={f("parent_id")}
                style={{ width:"100%", boxSizing:"border-box", background:"var(--surface)", border:`1px solid var(--border)`, borderRadius:6, padding:"8px 10px", fontSize:12, color:"var(--text)", fontFamily:"inherit", outline:"none", cursor:"pointer" }}
              >
                <option value="">— None (standalone / master) —</option>
                {allAffiliates
                  .filter(a =>
                    a.id !== affiliateId &&                          // not itself
                    a.parent_id !== affiliateId &&                   // not already a child of this one (would create a cycle)
                    a.status !== 'archived'
                  )
                  .map(a => (
                    <option key={a.id} value={a.id}>
                      {a.org_name} ({a.id})
                    </option>
                  ))
                }
              </select>
              <div style={{ fontSize:10, color:"var(--text-dim)", marginTop:4 }}>
                Setting this makes the current affiliate a sub-affiliate of the selected one.
              </div>
            </div>
          </div>
        </div>

        {/* ── Channel Allocations ─────────────────────────────────────── */}
        <div style={{ background:"var(--surface)", border:`1px solid var(--border)`, borderRadius:9, overflow:"hidden" }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"13px 18px", borderBottom:`1px solid var(--border)`, background:"var(--surface-alt)" }}>
            <div style={{ fontSize:11, fontWeight:700, color:"var(--text-dim)", textTransform:"uppercase", letterSpacing:"0.07em" }}>Channel Allocations</div>
            <span style={{ fontSize:11, color:"var(--text-sub)" }}>{currentChannels.length} assigned</span>
          </div>

          {currentChannels.length > 0 && (
            <table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead>
                <tr><TH>Channel ID</TH><TH>Display Name</TH><TH>SRT URL</TH><TH>Status</TH><TH>Action</TH></tr>
              </thead>
              <tbody>
                {currentChannels.map((ch) => (
                  <tr key={ch.id}>
                    <TD><Pill label={ch.id} /></TD>
                    <TD style={{ fontWeight:500 }}>{ch.display_name}</TD>
                    <TD mono sub style={{ maxWidth:200, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{ch.master_srt_url}</TD>
                    <TD><Badge status={ch.status} /></TD>
                    <TD>
                      <button
                        onClick={() => {
                          if (addedChannels.includes(ch.id)) {
                            setAddedChannels((p) => p.filter((c) => c !== ch.id));
                          } else {
                            setRemovedChannels((p) => [...p, ch.id]);
                          }
                        }}
                        style={{ background:"var(--red-bg)", border:`1px solid var(--red-border)`, borderRadius:5, padding:"3px 9px", fontSize:11, color:"var(--red)", cursor:"pointer", fontFamily:"inherit" }}
                      >
                        Remove
                      </button>
                    </TD>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {availableToAdd.length > 0 && (
            <div style={{ padding:"12px 18px", borderTop: currentChannels.length > 0 ? `1px solid var(--border)` : "none", display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
              <span style={{ fontSize:11, color:"var(--text-sub)", whiteSpace:"nowrap" }}>Add channel:</span>
              {availableToAdd.map((ch) => (
                <div
                  key={ch.id}
                  onClick={() => setAddedChannels((p) => [...p, ch.id])}
                  style={{ display:"flex", alignItems:"center", gap:6, padding:"4px 10px", borderRadius:5, border:`1px solid var(--border)`, background:"var(--surface-alt)", cursor:"pointer", fontSize:11 }}
                >
                  <span style={{ fontFamily:"'IBM Plex Mono', monospace", fontWeight:500, color:"var(--ink)" }}>{ch.id}</span>
                  <span style={{ color:"var(--text-sub)" }}>{ch.display_name}</span>
                  <span style={{ color:"var(--green)", fontWeight:700, fontSize:13 }}>+</span>
                </div>
              ))}
            </div>
          )}

          {currentChannels.length === 0 && availableToAdd.length === 0 && (
            <div style={{ padding:"20px 18px", fontSize:12, color:"var(--text-dim)" }}>No channels available to assign.</div>
          )}
        </div>

        {/* ── Device Allocations ──────────────────────────────────────── */}
        <div style={{ background:"var(--surface)", border:`1px solid var(--border)`, borderRadius:9, overflow:"hidden" }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"13px 18px", borderBottom:`1px solid var(--border)`, background:"var(--surface-alt)" }}>
            <div style={{ fontSize:11, fontWeight:700, color:"var(--text-dim)", textTransform:"uppercase", letterSpacing:"0.07em" }}>Device Allocations</div>
            <span style={{ fontSize:11, color:"var(--text-sub)" }}>
              {currentDevices.length + newDevices.length} device{currentDevices.length + newDevices.length !== 1 ? "s" : ""}
            </span>
          </div>

          {/* Existing devices table */}
          {currentDevices.length > 0 && (
            <table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead>
                <tr><TH>Device ID</TH><TH>MAC Address</TH><TH>Model</TH><TH>Channel</TH><TH>Status</TH><TH>Action</TH></tr>
              </thead>
              <tbody>
                {currentDevices.map((dev) => {
                  const isMulti = isMultiChannelDevice(dev);
                  const channelIds = isMulti
                    ? (Array.isArray(dev.channel_ids) ? dev.channel_ids : (dev.channel_id ? [dev.channel_id] : []))
                    : (dev.channel_id ? [dev.channel_id] : []);
                  const visibleChannelIds = channelIds.filter(chId =>
                    !optimisticallyRemoved.some(r => r.deviceId === dev.id && r.channelId === chId)
                  );

                  return (
                  <tr key={dev.id + (isMulti ? "" : dev.channel_id)}>
                    <TD mono style={{ fontWeight:500 }}>{dev.id}</TD>
                    <TD mono sub>{dev.mac_address}</TD>
                    <TD sub>{dev.device_type === "self-srt-connect" ? "" : (dev.device_model || "—")}</TD>
                    <TD>
                      {isMulti ? (
                        /* multi-channel models: show every assigned channel as a chip with individual remove */
                        <div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
                          {visibleChannelIds.length === 0 ? (
                            <span style={{ fontSize:11, color:"var(--text-dim)" }}>No channels</span>
                          ) : visibleChannelIds.map(chId => (
                            <span key={chId} style={{ display:"inline-flex", alignItems:"center", gap:4, background:"var(--surface-alt)", border:`1px solid var(--border)`, borderRadius:5, padding:"2px 8px", fontSize:11, fontFamily:"'IBM Plex Mono', monospace", color:"var(--ink)" }}>
                              {chId}
                              <button
                                onClick={async () => {
                                  setOptimisticallyRemoved(p => [...p, { deviceId: dev.id, channelId: chId }]);
              
                                  try {
                                    await api.devices.unassignChannel(dev.id, chId);
                                    const ch = currentChannels.find(c => c.id === chId);
                                    setPendingChannelRemoval({
                                      deviceId: dev.id,
                                      channelId: chId,
                                      channelUrl: ch?.master_srt_url || '',
                                    });
                                  } catch (e) {
                                    setOptimisticallyRemoved(p => p.filter(r => !(r.deviceId === dev.id && r.channelId === chId)));
                                    setError(e.message || "Failed to remove channel");
                                  }
                                }}
                                title={`Remove ${chId} from this device`}
                                style={{ background:"none", border:"none", color:"var(--text-dim)", cursor:"pointer", fontSize:12, lineHeight:1, padding:0, marginLeft:2 }}
                              >×</button>
                            </span>
                          ))}
                        </div>
                      ) : (
                        /* Single-channel: existing channel edit flow */
                        <>
                          {editingChannelFor === dev.id ? (
                            <div style={{ display:"flex", alignItems:"center", gap:5 }}>
                              <select
                                value={channelDraft}
                                onChange={(e) => setChannelDraft(e.target.value)}
                                autoFocus
                                style={{ background:"var(--surface)", border:`1px solid var(--border)`, borderRadius:5, padding:"3px 6px", fontSize:11, color:"var(--text)", fontFamily:"inherit", outline:"none", cursor:"pointer" }}
                              >
                                {currentChannels.map(c => (
                                  <option key={c.id} value={c.id}>
                                    {c.id} · {c.display_name}
                                    {takenChannels.includes(c.id) && c.id !== dev.channel_id ? " (in use)" : ""}
                                  </option>
                                ))}
                              </select>
                              <button onClick={() => handleChangeChannel(dev.id)} disabled={changingChannel}
                                style={{ background:"var(--ink)", border:"none", borderRadius:5, padding:"3px 9px", fontSize:11, fontWeight:600, color:"#fff", cursor: changingChannel ? "not-allowed" : "pointer", opacity: changingChannel ? 0.6 : 1, fontFamily:"inherit" }}>
                                {changingChannel ? "…" : "Save"}
                              </button>
                              <button onClick={() => { setEditingChannelFor(null); setChannelChangeError(null); }}
                                style={{ background:"var(--surface)", border:`1px solid var(--border)`, borderRadius:5, padding:"3px 8px", fontSize:11, color:"var(--text-sub)", cursor:"pointer", fontFamily:"inherit" }}>✕</button>
                            </div>
                          ) : (
                            <div
                              onClick={() => { setEditingChannelFor(dev.id); setChannelDraft(dev.channel_id || ""); setChannelChangeError(null); }}
                              style={{ cursor:"pointer", display:"inline-flex", alignItems:"center", gap:5 }}
                              title="Click to change channel"
                            >
                              {dev.channel_id ? <Pill label={dev.channel_id} /> : <span style={{ fontSize:12, color:"var(--text-dim)" }}>—</span>}
                              <span style={{ fontSize:10, color:"var(--text-dim)" }}>✎</span>
                            </div>
                          )}
                          {editingChannelFor === dev.id && channelChangeError && (
                            <div style={{ fontSize:10, color:"var(--red,#dc2626)", marginTop:4 }}>{channelChangeError}</div>
                          )}
                        </>
                      )}
                    </TD>
                    <TD><Badge status={dev.status} /></TD>
                    <TD>
                      <button
                        onClick={() => setRemovedDevices((p) => [...p, dev.id])}
                        style={{ background:"var(--red-bg)", border:`1px solid var(--red-border)`, borderRadius:5, padding:"3px 9px", fontSize:11, color:"var(--red)", cursor:"pointer", fontFamily:"inherit" }}
                      >
                        {isMulti ? "Remove Device" : "Remove"}
                      </button>
                    </TD>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {/* New device rows */}
          {newDevices.map((dev, i) => (
            <div
              key={i}
              style={{ padding:"14px 18px", borderTop:`1px solid var(--border)`, display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr 1fr auto", gap:12, alignItems:"end" }}
            >
              {/* Select existing device — searchable */}
              <div style={{ minWidth: 260 }}>
                <div style={{ fontSize:11, color:"var(--text-dim)", textTransform:"uppercase", letterSpacing:"0.07em", fontWeight:600, marginBottom:5 }}>
                  Existing Device
                </div>
                <DeviceCombobox
                  devices={(() => {
                    const unassigned = existingDevices.filter(d => !d.affiliate_id);
                    // Fall back to this affiliate's own devices only when there
                    // are no unassigned ones at all — keeps a previously-picked
                    // device for THIS affiliate selectable/re-pickable.
                    return unassigned.length > 0
                      ? unassigned
                      : existingDevices.filter(d => d.affiliate_id === affiliateId);
                  })()}
                  value={dev.existingId || ""}
                  onChange={(id) => selectExistingDevice(i, id)}
                />
              </div>

              {/* Device Type */}
              <div>
                <div style={{ fontSize:11, color:"var(--text-dim)", textTransform:"uppercase", letterSpacing:"0.07em", fontWeight:600, marginBottom:5 }}>Device Type</div>
                <select
                  value={dev.deviceType || "box"}
                  onChange={(e) => setNewDevices((p) => p.map((d, j) => j === i ? { ...d, deviceType: e.target.value } : d))}
                  disabled={!dev.isNew && !!dev.existingId}
                  style={{ width:"100%", background: !dev.isNew && dev.existingId ? "var(--surface-alt)" : "var(--surface)", border:`1px solid var(--border)`, borderRadius:6, padding:"7px 9px", fontSize:12, color:"var(--text)", fontFamily:"inherit", outline:"none", cursor: !dev.isNew && dev.existingId ? "default" : "pointer", opacity: !dev.isNew && dev.existingId ? 0.7 : 1 }}
                >
                  <option value="box">Box (eBox device)</option>
                  <option value="self-srt-connect">Self-SRT-Connect (direct internet, IP only)</option>
                </select>
              </div>

              {/* MAC Address — box devices only */}
              {dev.deviceType !== "self-srt-connect" && (
              <div>
                <div style={{ fontSize:11, color:"var(--text-dim)", textTransform:"uppercase", letterSpacing:"0.07em", fontWeight:600, marginBottom:5 }}>MAC Address</div>
                <input
                  value={dev.mac}
                  onChange={(e) => setNewDevices((p) => p.map((d, j) => j === i ? { ...d, mac: e.target.value } : d))}
                  placeholder="AA:BB:CC:DD:EE:FF"
                  disabled={!dev.isNew && !!dev.existingId}
                  style={{ width:"100%", background: !dev.isNew && dev.existingId ? "var(--surface-alt)" : "var(--surface)", border:`1px solid ${dev.mac && dev.isNew !== false && !validMAC(dev.mac) ? "var(--red)" : "var(--border)"}`, borderRadius:6, padding:"7px 9px", fontSize:12, color:"var(--text)", fontFamily:"'IBM Plex Mono', monospace", outline:"none", boxSizing:"border-box", opacity: !dev.isNew && dev.existingId ? 0.7 : 1 }}
                />
                {dev.mac && dev.isNew !== false && !validMAC(dev.mac) && (
                  <div style={{ fontSize:11, color:"var(--red)", marginTop:3 }}>Invalid format</div>
                )}
              </div>
              )}

              {/* IP Address — optional for box, required for self-srt-connect */}
              <div>
                <div style={{ fontSize:11, color:"var(--text-dim)", textTransform:"uppercase", letterSpacing:"0.07em", fontWeight:600, marginBottom:5 }}>
                  IP Address {dev.deviceType === "self-srt-connect"
                    ? <span style={{ fontWeight:700 }}>*</span>
                    : <span style={{ fontWeight:400, textTransform:"none" }}>(optional)</span>}
                </div>
                <input
                  value={dev.ip_address || ""}
                  onChange={(e) => setNewDevices((p) => p.map((d, j) => j === i ? { ...d, ip_address: e.target.value } : d))}
                  placeholder="192.168.1.10"
                  disabled={!dev.isNew && !!dev.existingId}
                  style={{ width:"100%", background: !dev.isNew && dev.existingId ? "var(--surface-alt)" : "var(--surface)", border:`1px solid var(--border)`, borderRadius:6, padding:"7px 9px", fontSize:12, color:"var(--text)", fontFamily:"'IBM Plex Mono', monospace", outline:"none", boxSizing:"border-box", opacity: !dev.isNew && dev.existingId ? 0.7 : 1 }}
                />
                {dev.deviceType === "self-srt-connect" && (
                  <div style={{ fontSize:10, color:"var(--text-dim)", marginTop:4 }}>No box, no MAC — whitelisted by IP directly.</div>
                )}
              </div>

              {/* Device Model — box devices only */}
              {dev.deviceType !== "self-srt-connect" && (
              <div>
                <div style={{ fontSize:11, color:"var(--text-dim)", textTransform:"uppercase", letterSpacing:"0.07em", fontWeight:600, marginBottom:5 }}>Device Model</div>
                <select
                  value={dev.model}
                  onChange={(e) => setNewDevices((p) => p.map((d, j) => j === i ? { ...d, model: e.target.value } : d))}
                  disabled={!dev.isNew && !!dev.existingId}
                  style={{ width:"100%", background: !dev.isNew && dev.existingId ? "var(--surface-alt)" : "var(--surface)", border:`1px solid var(--border)`, borderRadius:6, padding:"7px 9px", fontSize:12, color:"var(--text)", fontFamily:"inherit", outline:"none", cursor: !dev.isNew && dev.existingId ? "default" : "pointer", opacity: !dev.isNew && dev.existingId ? 0.7 : 1 }}
                >
                  {allModels.length === 0
                    ? <option value="">No models in DB</option>
                    : allModels.map((m) => <option key={m} value={m}>{m}</option>)
                  }
                </select>
              </div>
              )}

              {/* Channel */}
              <div>
                <div style={{ fontSize:11, color:"var(--text-dim)", textTransform:"uppercase", letterSpacing:"0.07em", fontWeight:600, marginBottom:5 }}>
                  Channel{isMultiChannelDevice(dev) ? `s (up to ${maxChannelsForDevice(dev)})` : ""}
                </div>

                {isMultiChannelDevice(dev) ? (() => {
                  const MAX_3568 = maxChannelsForDevice(dev);
                  const picked = Array.isArray(dev.channelIds) ? dev.channelIds : (dev.channelId ? [dev.channelId] : []);
                  const blocked = channelsBlockedForRow(i);
                  const available = currentChannels.filter(c => !picked.includes(c.id) && !blocked.includes(c.id));
                  const slotsLeft = MAX_3568 - picked.length;
                  return (
                    <div>
                      {picked.length > 0 && (
                        <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:8 }}>
                          {picked.map(chId => {
                            const ch = currentChannels.find(c => c.id === chId);
                            return (
                              <span key={chId} style={{ display:"inline-flex", alignItems:"center", gap:5, background:"var(--ink)", color:"#fff", borderRadius:5, padding:"4px 10px", fontSize:11, fontFamily:"'IBM Plex Mono', monospace" }}>
                                {chId}{ch ? ` · ${ch.display_name}` : ""}
                                <button onClick={() => setNewDevices(p => p.map((d, j) => j === i ? { ...d, channelIds: picked.filter(id => id !== chId) } : d))}
                                  style={{ background:"none", border:"none", color:"rgba(255,255,255,0.7)", cursor:"pointer", fontSize:13, lineHeight:1, padding:0 }}>×</button>
                              </span>
                            );
                          })}
                        </div>
                      )}
                      {slotsLeft > 0 ? (
                        <select value="" onChange={(e) => { if (!e.target.value) return; setNewDevices(p => p.map((d, j) => j === i ? { ...d, channelIds: [...picked, e.target.value] } : d)); }}
                          style={{ width:"100%", background:"var(--surface)", border:`1px solid var(--border)`, borderRadius:6, padding:"7px 9px", fontSize:12, color:"var(--text)", fontFamily:"inherit", outline:"none", cursor:"pointer" }}>
                          <option value="">— Add channel ({slotsLeft} slot{slotsLeft !== 1 ? "s" : ""} remaining) —</option>
                          {available.map(c => <option key={c.id} value={c.id}>{c.id} — {c.display_name}</option>)}
                        </select>
                      ) : <div style={{ fontSize:11, color:"var(--text-dim)" }}>All 6 slots filled.</div>}
                    </div>
                  );
                })() : (
                  <select
                    value={dev.channelId}
                    onChange={(e) => setNewDevices((p) => p.map((d, j) => j === i ? { ...d, channelId: e.target.value } : d))}
                    style={{ width:"100%", background:"var(--surface)", border:`1px solid var(--border)`, borderRadius:6, padding:"7px 9px", fontSize:12, color:"var(--text)", fontFamily:"inherit", outline:"none", cursor:"pointer" }}
                  >
                    <option value="">— Select channel —</option>
                    {currentChannels
                      .filter((c) => {
                        const blocked = channelsBlockedForRow(i);
                        return !blocked.includes(c.id) || c.id === dev.channelId;
                      })
                      .map((c) => <option key={c.id} value={c.id}>{c.id} — {c.display_name}</option>)}
                  </select>
                )}
              </div>

              {/* Remove row */}
              <button
                onClick={() => setNewDevices((p) => p.filter((_, j) => j !== i))}
                style={{ background:"none", border:`1px solid var(--border)`, borderRadius:5, padding:"7px 10px", fontSize:14, color:"var(--text-dim)", cursor:"pointer", alignSelf:"center", lineHeight:1 }}
              >
                ×
              </button>
            </div>
          ))}

          {/* Add device button */}
          <div style={{ padding:"12px 18px", borderTop: currentDevices.length > 0 || newDevices.length > 0 ? `1px solid var(--border)` : "none" }}>
            <button
              onClick={() => setNewDevices((p) => [...p, { deviceType:"box", mac:"", model: allModels[0] || "", channelId:"", ip_address:"", isNew:true, existingId:null }])}
              style={{ display:"flex", alignItems:"center", gap:6, background:"transparent", border:`1px dashed var(--border-strong)`, borderRadius:6, padding:"7px 14px", color:"var(--text-sub)", cursor:"pointer", fontSize:12, fontFamily:"inherit" }}
            >
              <span style={{ fontSize:16, lineHeight:1 }}>+</span> Add Device
            </button>
          </div>
        </div>

        {/* ── Save / Cancel ───────────────────────────────────────────── */}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", paddingTop:4 }}>
          <Btn onClick={() => onBack && onBack()}>Cancel</Btn>
          <Btn primary onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save Changes"}
          </Btn>
        </div>
      </div>

      <UndoConfirm
        open={!!pendingChannelRemoval}
        title={`Channel ${pendingChannelRemoval?.channelId} removed`}
        message={`${pendingChannelRemoval?.channelId} has been unassigned from device ${pendingChannelRemoval?.deviceId}.`}
        undoSeconds={5}
        onConfirm={async () => {
          // Capture before clearing — the state setter is async and reading the
          // value back after nulling it only works by accident of closure.
          const { deviceId, channelId } = pendingChannelRemoval;
          setPendingChannelRemoval(null);
          // Refetch BEFORE dropping the optimistic mask. The mask is the only
          // thing hiding the removed chip, and `data`/`existingDevices` still
          // hold the channel_ids from the load() that ran before the unassign.
          // Clearing the mask against stale data is what made a removed channel
          // reappear — an operator would then try to remove it again and get a
          // confusing error. load() also refreshes takenChannels, so the channel
          // becomes selectable elsewhere immediately.
          await load();
          setOptimisticallyRemoved(p => p.filter(r => !(r.deviceId === deviceId && r.channelId === channelId)));
        }}
        onCancel={async () => {
          const { deviceId, channelId, channelUrl } = pendingChannelRemoval;
          setPendingChannelRemoval(null);
          try {
            await api.devices.assignChannel(deviceId, {
              channel_id: channelId,
              channel_url: channelUrl || '',
              display_port: 1,
              audio_device: 'default',
            });
          } catch (e) {
            setError(`Undo failed: ${e.message || "Could not reassign channel"}`);
          }
          // Reload on BOTH paths: after a successful undo the channel is back
          // and the table must show it, and after a FAILED undo the table must
          // show the server's real state rather than the pre-removal one.
          await load();
          setOptimisticallyRemoved(p => p.filter(r => !(r.deviceId === deviceId && r.channelId === channelId)));
        }}
      />
    </>
  );
}
